import {useEffect, useState} from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {Box, Button, Typography, useTheme} from '@mui/material';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {DatePickerProps} from '..';
import {Image} from '@jobseeker/lotus/components/Image';
import {ChevronDown} from '@jobseeker/lotus/icons';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import {DateView} from '@mui/x-date-pickers/models';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

const SelectIcon = ({className, open}: any) => {
  const theme = useTheme();
  return (
    <Image
      className={className}
      src={ChevronDown}
      alt={'Drop Down Arrow'}
      width={12}
      height={12}
      style={{
        userSelect: 'none',
        padding: '2px',
        zIndex: open ? 2 : 0,
        marginRight: '7px',
        backgroundColor: 'white',
        transform: open ? 'rotate(180deg)' : 'none',
      }}
      objectFit="cover"
    />
  );
};
const Datepicker = (props: DatePickerProps) => {
  const {
    value,
    onChange,
    name,
    views,
    openTo,
    sx,
    format,
    disabled = false,
    minDate = dayjs('1970-01-01'),
    maxDate = dayjs(),
    setOpenDatePicker,
  } = props;
  const [dateValue, setDateValue] = useState<Dayjs | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState<DateView>(openTo || 'year');
  const isLarge = useIsLargeDevice();
  const handleViewChange = (newView: DateView) => {
    if (newView === 'month' || newView === 'year') {
      setCurrentView(newView);
    }
  };
  const handleInternalViewChange = (newView: DateView) => {
    setCurrentView(newView);
  };
  useEffect(() => {
    if (value) {
      setDateValue(dayjs(new Date(value)));
    } else {
      setDateValue(null);
    }
  }, [value]);

  useEffect(() => {
    const dateEvent = {
      target: {
        name: name ?? '',
        value: '',
      },
    };
    let finalValue: string[] = [];
    views &&
      views.map(view => {
        if (dateValue && view === 'year') {
          finalValue.push((dateValue ? dateValue?.year() : 0).toString());
        } else if (dateValue && view === 'month') {
          finalValue.push(
            ((dateValue ? dateValue?.month() : 0) + 1)
              .toString()
              .padStart(2, '0')
          );
        } else if (dateValue && view === 'day') {
          finalValue.push((dateValue ? dateValue?.day() : 0).toString());
        }
      });
    dateEvent.target.value = finalValue.join('-');
    onChange && finalValue.length && onChange(dateEvent);
  }, [dateValue]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={dateValue}
        onChange={setDateValue}
        views={views}
        name={name}
        openTo={currentView}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        onViewChange={handleInternalViewChange}
        sx={{
          width: '100%',
          '& .MuiInputBase-input': {
            fontSize: '14px',
            fontWeight: 500,
            color: 'secondary.main',
          },
          '.MuiInputBase-root': {
            '& fieldset': {
              borderColor: '#7E7E7E33',
            },
            '&:hover fieldset': {
              border: '1px solid #7E7E7E33',
            },
            '&:disabled fieldset': {
              border: '1px solid #7E7E7E33',
            },
            '&.Mui-focused fieldset': {
              border: '1px solid #7E7E7E33',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #7E7E7E33',
            },
          },
          '.MuiPickersCalendarHeader-root': {
            display: 'none',
          },
          svg: {
            width: 18,
          },
          ...sx,
        }}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        slotProps={{
          textField: {
            size: 'small',
            InputProps: {
              sx: {borderRadius: 8, height: 40},
              readOnly: true,
              onMouseDown: event => event.preventDefault(),
            },
            onClick: () => {
              if (isLarge) {
                setIsOpen(true);
              } else {
                setOpenDatePicker(name);
              }
            },
          },
          calendarHeader: {
            sx: {
              display: 'none',
            },
          },
          desktopTransition: {
            style: {
              marginTop: 2,
            },
          },
          layout: {
            sx: {
              display: 'block',
              '& .MuiDateCalendar-root': {
                height: 'fit-content',
              },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.12)',
              borderRadius: '8px',
              marginTop: {xxs: 0, md: '5.6px'},
              marginBottom: {xxs: 0, md: '5.6px'},
            },
          },
          yearButton: {
            sx: {
              ':hover': {
                backgroundColor: isAppIimjobs
                  ? '#91B87929'
                  : 'rgba(233, 99, 12, 0.08)',
              },
            },
          },
          monthButton: {
            sx: {
              ':hover': {
                backgroundColor: isAppIimjobs
                  ? '#91B87929'
                  : 'rgba(233, 99, 12, 0.08)',
              },
            },
          },
          openPickerButton: {
            sx: {
              display: disabled ? 'none' : 'flex',
              backgroundColor: 'white !important',
              color: 'white !important',
            },
          },
        }}
        slots={{
          openPickerIcon: iconProps => (
            <SelectIcon {...iconProps} open={isOpen} />
          ),
          toolbar: () => (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                background: isAppIimjobs
                  ? '#91B87929'
                  : 'linear-gradient(90deg, #E3E9F0 0%, #F3F5FB 100%)',
                gap: '55px',
                height: '43px',
              }}
            >
              <Box
                sx={{
                  width: '86px',
                  textAlign: 'center',
                }}
              >
                {currentView === 'month' ? (
                  <ButtonV2
                    onClick={() => handleViewChange('month')}
                    variant="contained"
                    color="primary"
                    sx={{
                      width: '86px',
                      height: '26px',
                      fontSize: '14px',
                      marginTop: '10px',
                      fontWeight: 500,
                    }}
                  >
                    Month
                  </ButtonV2>
                ) : (
                  <Typography
                    onClick={() => handleViewChange('month')}
                    sx={{
                      fontSize: '14px',
                      color: '#333333',
                      cursor: 'pointer',
                      lineHeight: '26px',
                      marginTop: '11px',
                      fontWeight: 500,
                    }}
                  >
                    Month
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  width: '86px',
                  textAlign: 'center',
                }}
              >
                {currentView === 'year' ? (
                  <ButtonV2
                    onClick={() => handleViewChange('year')}
                    variant="contained"
                    color="primary"
                    sx={{
                      width: '86px',
                      height: '26px',
                      fontSize: '14px',
                      marginTop: '10px',
                      fontWeight: 500,
                    }}
                  >
                    Year
                  </ButtonV2>
                ) : (
                  <Typography
                    onClick={() => handleViewChange('year')}
                    sx={{
                      fontSize: '14px',
                      color: '#333333',
                      cursor: 'pointer',
                      lineHeight: '26px',
                      marginTop: '11px',
                      fontWeight: 500,
                    }}
                  >
                    Year
                  </Typography>
                )}
              </Box>
            </Box>
          ),
        }}
        format={format}
      />
    </LocalizationProvider>
  );
};

export default Datepicker;
